// Suggests only areas we actually have developments in - counties, towns/areas
// and the developments themselves, embedded by the server as JSON (see SearchAreaSuggestions.php) -
// rather than Google Places, which suggested places with no developments and
// missed neighbourhoods it only knows as a sublocality (e.g. Kings Heath).
//
// Implements the WAI-ARIA "combobox with list autocomplete" pattern: the
// input keeps real DOM focus throughout, Up/Down move a *virtual* highlight
// via aria-activedescendant, Enter selects whichever option is highlighted,
// and Escape closes the list without moving focus (it never left the input).
// See https://www.w3.org/WAI/ARIA/apg/patterns/combobox/examples/combobox-autocomplete-list/

const MIN_QUERY_LENGTH = 2
const MAX_SUGGESTIONS = 10

// Tie-break between equally good matches - broadest area first.
const TYPE_ORDER = { county: 0, town: 1, development: 2 }

// Case/accent/punctuation-insensitive form used for matching, so e.g.
// "st ives", "St. Ives" and "kings heath" all match as expected.
const normalize = (value) => value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/['’.]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()

// Whether any word in (already normalized) text starts with the query - which
// may itself span several words, e.g. "upon thames".
const hasWordStartingWith = (text, query) => ` ${text}`.includes(` ${query}`)

// Lower is better: whole name starts with the query, then any word in the
// name does, then a word of its town/county does (so "kent" also lists Kent's
// towns and developments), then a plain substring match anywhere in the name. -1 = no match.
const scoreSuggestion = (suggestion, query) => {
    if (suggestion.normalizedLabel.startsWith(query)) return 0
    if (hasWordStartingWith(suggestion.normalizedLabel, query)) return 1
    if (hasWordStartingWith(suggestion.normalizedDetail, query)) return 2
    if (suggestion.normalizedLabel.includes(query)) return 3
    return -1
}

const readSuggestions = (searchForm) => {
    const dataEl = searchForm.querySelector('.js-place-autocomplete-data')
    if (!dataEl) return []
    try {
        return JSON.parse(dataEl.textContent).map(suggestion => ({
            ...suggestion,
            // One format for every type: "Town, County",
            // "Development, Town, County", or just "County".
            text: [suggestion.label, suggestion.detail].filter(Boolean).join(', '),
            normalizedLabel: normalize(suggestion.label),
            normalizedDetail: normalize(suggestion.detail || ''),
        }))
    } catch {
        return []
    }
}

export const initAreaAutocomplete = (searchForm) => {
    const latitudeField = searchForm.querySelector('input[name="latitude"]');
    const longitudeField = searchForm.querySelector('input[name="longitude"]');
    const sortField = searchForm.querySelector('input[name="sort"]');
    const searchInput = searchForm.querySelector('input.js-place-autocomplete');
    const searchOutput = searchForm.querySelector('.js-place-autocomplete-output');
    // The scrollable list (searchOutput) sits inside a plain positioning
    // wrapper (.search-autocomplete-panel) that must stay overflow-visible
    // for its own ::before gap-fill effect - so open/closed visibility
    // toggles on that wrapper, not on searchOutput itself.
    const searchOutputWrapper = searchOutput?.closest('.search-autocomplete-panel');
    const statusEl = searchForm.querySelector('.js-place-autocomplete-status');
    if (!latitudeField || !longitudeField || !searchInput || !searchOutput || !searchOutputWrapper) return

    const suggestions = readSuggestions(searchForm)
    if (!suggestions.length) return

    let activeIndex = -1

    const getOptions = () => [...searchOutput.querySelectorAll('.place-autocomplete-option')]

    const setActiveIndex = (index) => {
        const options = getOptions()
        activeIndex = index
        options.forEach((option, i) => {
            const isActive = i === index
            option.classList.toggle('is-active', isActive)
            option.setAttribute('aria-selected', isActive ? 'true' : 'false')
        })
        if (index === -1) {
            searchInput.removeAttribute('aria-activedescendant')
        } else {
            searchInput.setAttribute('aria-activedescendant', options[index].id)
            options[index].scrollIntoView({ block: 'nearest' })
        }
    }

    const openList = () => {
        searchOutputWrapper.classList.remove('hidden')
        searchInput.setAttribute('aria-expanded', 'true')
    }

    const closeList = () => {
        searchOutputWrapper.classList.add('hidden')
        searchInput.setAttribute('aria-expanded', 'false')
        searchInput.removeAttribute('aria-activedescendant')
        activeIndex = -1
    }

    const setLocation = (lat, lng) => {
        const hasLocation = lat !== null && lng !== null
        latitudeField.value = hasLocation ? lat : ''
        latitudeField.disabled = !hasLocation
        longitudeField.value = hasLocation ? lng : ''
        longitudeField.disabled = !hasLocation
        if (sortField) {
            sortField.disabled = !hasLocation
        }
    }

    const selectOption = (suggestion) => {
        searchInput.value = suggestion.text
        setLocation(suggestion.lat, suggestion.lng)
        closeList()
        searchForm.requestSubmit()
    }

    const buildOption = (suggestion, index) => {
        // A plain div, not a real button - keyboard users navigate
        // options via the input's own Up/Down/Enter handling below,
        // not by tabbing/focusing into these individually.
        const optionEl = document.createElement('div');
        optionEl.id = `${searchOutput.id}-option-${index}`;
        optionEl.setAttribute('role', 'option');
        optionEl.setAttribute('aria-selected', 'false');
        optionEl.classList.add('place-autocomplete-option');
        // Text lives in its own span (not optionEl.textContent
        // directly) so the h4 class sits on the text-containing
        // element itself - capsize's baseline trim (capsize.css)
        // targets .h4 with ::before/::after, which only works when
        // applied directly to the element wrapping the text.
        const optionText = document.createElement('span');
        optionText.classList.add('h4');
        optionText.textContent = suggestion.text;
        optionEl.append(optionText);
        // Stops the click's mousedown from shifting focus off the
        // input - the click event itself still fires as normal.
        optionEl.addEventListener('mousedown', (e) => e.preventDefault());
        optionEl.addEventListener('click', () => selectOption(suggestion));
        return optionEl
    }

    searchInput.addEventListener('input', () => {
        // Typing again invalidates any location picked earlier (or filled by
        // "Use my current location"), so a free-text search isn't silently
        // run against the old coordinates.
        setLocation(null, null)

        const query = normalize(searchInput.value)
        if (query.length < MIN_QUERY_LENGTH) {
            closeList();
            if (statusEl) statusEl.textContent = ''
            return
        }

        const matches = suggestions
            .map(suggestion => ({ suggestion, score: scoreSuggestion(suggestion, query) }))
            .filter(({ score }) => score !== -1)
            .sort((a, b) => a.score - b.score
                || TYPE_ORDER[a.suggestion.type] - TYPE_ORDER[b.suggestion.type]
                || a.suggestion.label.localeCompare(b.suggestion.label))
            .slice(0, MAX_SUGGESTIONS)

        getOptions().forEach(el => el.remove())

        if (matches.length) {
            const suggestionEls = new DocumentFragment();
            matches.forEach(({ suggestion }, index) => suggestionEls.append(buildOption(suggestion, index)))
            searchOutput.append(suggestionEls);
            openList();
            setActiveIndex(-1);
            if (statusEl) {
                statusEl.textContent = `${matches.length} suggestion${matches.length === 1 ? '' : 's'} available`
            }
        } else {
            closeList();
            if (statusEl) statusEl.textContent = 'No suggestions found'
        }
    })

    searchInput.addEventListener('keydown', (e) => {
        const isOpen = searchInput.getAttribute('aria-expanded') === 'true'

        if (e.key === 'ArrowDown') {
            if (!isOpen) return
            const options = getOptions()
            if (!options.length) return
            e.preventDefault()
            setActiveIndex((activeIndex + 1) % options.length)
            return
        }

        if (e.key === 'ArrowUp') {
            if (!isOpen) return
            const options = getOptions()
            if (!options.length) return
            e.preventDefault()
            setActiveIndex((activeIndex - 1 + options.length) % options.length)
            return
        }

        if (e.key === 'Enter') {
            if (!isOpen || activeIndex === -1) return
            const option = getOptions()[activeIndex]
            if (!option) return
            // Selecting a suggestion, not submitting the form.
            e.preventDefault()
            option.dispatchEvent(new MouseEvent('click', { bubbles: true }))
            return
        }

        if (e.key === 'Escape') {
            if (!isOpen) return
            e.preventDefault()
            closeList()
        }
    })

    // Closes the list when focus genuinely leaves the input (Tab to the
    // Search button, click elsewhere) - a click on an option itself never
    // reaches here, since its mousedown handler above keeps focus put.
    searchInput.addEventListener('blur', () => closeList())
}
